// ai-sidebar/ai-assistant-sidebar.tsx
// Top-level Page Assistant. Two render branches (expanded panel / collapsed
// rail) driven by collapse state. This file owns the public interface that the
// app's mount sites already depend on, so it keeps the legacy export name
// `AIAssistantSidebar` and the same props.
import { useEffect, useRef } from "react";
import "./ai-sidebar.css";
import { AssistantProvider, useAssistant } from "./use-assistant";
import { AssistantHeader } from "./components/assistant-header";
import { Launcher } from "./components/launcher";
import { MessageThread } from "./components/message-thread";
import { Composer } from "./components/composer";
import { AiAssistantCollapsed } from "./ai-assistant-collapsed";
import type { Phase } from "./types";

export type { Phase };

export interface AIAssistantSidebarProps {
  phase?: Phase;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  /** Optional explicit brand; falls back to the app's stored brand. */
  brandName?: string | null;
  initialTopic?: string;
  contextualUpdate?: { title: string; description: string; timestamp?: number };
  externalQuestion?: { text: string; ts: number };
}

function Shell({
  initialTopic,
  externalQuestion,
}: Pick<AIAssistantSidebarProps, "initialTopic" | "externalQuestion">) {
  const { collapsed, setCollapsed, send, messages } = useAssistant();

  // Cmd/Ctrl-K expands the rail.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (collapsed) setCollapsed(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [collapsed, setCollapsed]);

  // Legacy entry points: a one-shot initial topic and externally-injected asks.
  const firedInitial = useRef(false);
  useEffect(() => {
    if (initialTopic && !firedInitial.current && messages.length === 0) {
      firedInitial.current = true;
      void send(initialTopic);
    }
  }, [initialTopic, messages.length, send]);

  const lastExtTs = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (externalQuestion?.text && externalQuestion.ts !== lastExtTs.current) {
      lastExtTs.current = externalQuestion.ts;
      void send(externalQuestion.text);
    }
  }, [externalQuestion?.ts, externalQuestion?.text, send]);

  if (collapsed) return <AiAssistantCollapsed />;

  return (
    <div
      className="ai-sidebar ai-sidebar-tray fixed right-0 top-0 bottom-0 z-50 flex flex-col border-l border-border bg-background"
      style={{ width: 380, boxShadow: "var(--elevation-lg)" }}
    >
      <AssistantHeader />
      {messages.length === 0 ? (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Launcher />
        </div>
      ) : (
        <MessageThread />
      )}
      <Composer />
    </div>
  );
}

export function AIAssistantSidebar({
  phase = "general",
  isCollapsed,
  onToggleCollapse,
  brandName,
  initialTopic,
  externalQuestion,
}: AIAssistantSidebarProps) {
  return (
    <AssistantProvider
      phase={phase}
      brandName={brandName}
      collapsed={isCollapsed}
      onCollapsedChange={onToggleCollapse}
    >
      <Shell initialTopic={initialTopic} externalQuestion={externalQuestion} />
    </AssistantProvider>
  );
}

export default AIAssistantSidebar;
